import { topologicalSort } from '../lib/workflowGraph';
import type { ProposedAction, ProposedActionVisibility } from './types';

export interface EnrichedActiveProposedAction {
  action: ProposedAction;
  visibility: ProposedActionVisibility;
  unmetDependencyExternalIds: string[];
}

function hasSucceeded(action: ProposedAction | undefined): boolean {
  return action?.run_status === 'Succeeded';
}

export function deriveUnmetDependencies(
  action: ProposedAction,
  allActions: readonly ProposedAction[]
): string[] {
  const actionByExternalId = new Map<string, ProposedAction>();
  for (const candidate of allActions) {
    actionByExternalId.set(candidate.external_id, candidate);
  }

  const unmet: string[] = [];
  for (const dependencyExternalId of action.depends_on_action_external_ids) {
    if (!hasSucceeded(actionByExternalId.get(dependencyExternalId))) {
      unmet.push(dependencyExternalId);
    }
  }

  return unmet;
}

export function deriveVisibility(
  action: ProposedAction,
  allActions: readonly ProposedAction[]
): ProposedActionVisibility {
  if (action.todo_status !== 'Open') {
    return 'closed';
  }

  switch (action.run_status) {
    case 'Running':
      return 'running';
    case 'Succeeded':
      return 'succeeded';
    case 'Failed':
      return 'failed';
    case 'Idle':
    case undefined:
      break;
    default:
      break;
  }

  switch (action.decision) {
    case 'Pending':
      return 'pending';
    case 'Rejected':
      return 'closed';
    case 'Approved': {
      const unmetDependencyExternalIds = deriveUnmetDependencies(action, allActions);
      return unmetDependencyExternalIds.length === 0 ? 'executable' : 'blocked';
    }
    default:
      return 'closed';
  }
}

export function selectActiveActions(actions: readonly ProposedAction[]): ProposedAction[] {
  const openActions = actions.filter(
    (action) => action.todo_status === 'Open' && action.decision !== 'Rejected'
  );
  return topologicalSort(openActions);
}

export function selectEnrichedActiveActions(
  allActions: readonly ProposedAction[]
): EnrichedActiveProposedAction[] {
  const activeActions = selectActiveActions(allActions);
  return activeActions.map((action) => ({
    action,
    visibility: deriveVisibility(action, allActions),
    unmetDependencyExternalIds: deriveUnmetDependencies(action, allActions),
  }));
}
